import { IBookingSession } from 'client_react/booking/types';

const getIntlTimeZoneName = (
    timezone: string,
    date: Date,
    timeZoneName: Intl.DateTimeFormatOptions['timeZoneName']
) =>
    new Intl.DateTimeFormat([], { timeZone: timezone, timeZoneName })
        .formatToParts(date)
        .find((x) => x.type === 'timeZoneName')?.value;

/**
 * Short timezone label (`MDT`) for the given ISO instant, or today when omitted.
 * When the ISO string carries an offset that the browser's tzdata disagrees with
 * (stale rules, e.g. Alberta after 2026-11-01), falls back to the server's offset
 * (`GMT-06:00`) so the label never contradicts the displayed wall-clock time.
 */
export const getTimeZoneAbbreviation = (timezone: string, iso?: string) => {
    if (!timezone) {
        return '';
    }

    const date = iso ? new Date(iso) : new Date();

    if (Number.isNaN(date.getTime())) {
        return getIntlTimeZoneName(timezone, new Date(), 'short');
    }

    const isoOffset = iso ? getIsoOffsetLabel(iso) : null;

    if (isoOffset) {
        // Intl renders UTC itself as a bare `GMT`
        const intlOffset = getIntlTimeZoneName(timezone, date, 'longOffset');
        const normalizedIntlOffset = intlOffset === 'GMT' ? 'GMT+00:00' : intlOffset;

        if (normalizedIntlOffset !== isoOffset) {
            return isoOffset;
        }
    }

    return getIntlTimeZoneName(timezone, date, 'short');
};

// Formats TZ like -5:00 America/New_York
export const getTimeZoneName = (timezone: string) => {
    if (!timezone) {
        return '';
    }

    return Intl.DateTimeFormat([], { timeZone: timezone })
        .resolvedOptions()
        .timeZone.replace('_', ' ')
        .replace('/', ', ');
};

/**
 * Offset label from a server ISO-8601 string (`GMT-06:00`), independent of browser tzdata.
 * Returns null when the string has no numeric offset.
 */
export const getIsoOffsetLabel = (iso: string): string | null => {
    if (iso.endsWith('Z')) {
        return 'GMT+00:00';
    }

    const match = iso.match(/([+-])(\d{2}):(\d{2})$/);

    if (!match) {
        return null;
    }

    return `GMT${match[1]}${match[2]}:${match[3]}`;
};

export const formatTimezone = (timezone?: string, slotIso?: string) => {
    if (!timezone) {
        return '';
    }

    // Prefer the slot's own offset so the heading matches the selected date
    // even when browser/OS tzdata is stale (Alberta after 2026-11-01).
    const offsetFromSlot = slotIso ? getIsoOffsetLabel(slotIso) : null;
    let offset = offsetFromSlot || '';

    if (!offset) {
        const utcDate = new Date(Date.UTC(2023, 0, 1));
        const formatter = new Intl.DateTimeFormat([], {
            timeZone: timezone,
            timeZoneName: 'longOffset'
        });
        const parts = formatter.formatToParts(utcDate);
        offset = parts.find((part) => part.type === 'timeZoneName')?.value || '';
    }

    return `(${offset}) ${getTimeZoneName(timezone)}`;
};

/**
 * Wall-clock components from a server ISO-8601 string (ignores browser IANA rules).
 * Used so booking UIs stay correct when OS/browser tzdata lags legal changes.
 */
export const getIsoWallClockParts = (iso: string) => {
    const match = iso.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);

    if (!match) {
        return null;
    }

    return {
        year: Number(match[1]),
        month: Number(match[2]),
        day: Number(match[3]),
        hours: Number(match[4]),
        minutes: Number(match[5])
    };
};

/**
 * Date whose UTC components match the ISO wall clock (no timezone conversion).
 * Built in UTC so a wall-clock time inside the viewer's own DST gap isn't shifted;
 * read it with `getUTC*` or format it with `timeZone: 'UTC'`.
 */
export const createFloatingDateFromIso = (iso: string): Date => {
    const parts = getIsoWallClockParts(iso);

    if (!parts) {
        return new Date(iso);
    }

    return new Date(Date.UTC(parts.year, parts.month - 1, parts.day, parts.hours, parts.minutes));
};

export const formatIsoWallClockDate = (
    iso: string,
    options: Intl.DateTimeFormatOptions = { dateStyle: 'full' }
): string => createFloatingDateFromIso(iso).toLocaleDateString([], { ...options, timeZone: 'UTC' });

export const formatIsoWallClockTime = (
    iso: string,
    options: Intl.DateTimeFormatOptions = { timeStyle: 'short' }
): string => createFloatingDateFromIso(iso).toLocaleTimeString([], { ...options, timeZone: 'UTC' });

export const convertIsoToMinutes = (iso: string): number => {
    const parts = getIsoWallClockParts(iso);

    if (!parts) {
        return 0;
    }

    return parts.hours * 60 + parts.minutes;
};

export const convertTimeToMinutes = (time: Date): number => {
    return time.getHours() * 60 + time.getMinutes();
};

export const sanitizeNumber = (numberString: string): number => {
    return Number(numberString.replace(/[^\d.]/g, ''));
};

export const getFullPaymentAmount = (session: IBookingSession) =>
    sanitizeNumber(session.priceWithSalesTax);

export const getPartialPaymentAmount = (session: IBookingSession) =>
    sanitizeNumber(session.retainerPaymentAmountWithSalesTax);
